<script setup lang="ts">
import {
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  IotDeviceStateOfUse,
  PtwRegistrationState,
  QaEquipmentRtSystemDto,
  ReferencingObjectDto,
} from '@/api/generator';
import { useDeviceStatusSignalR } from '@/components/deviceStatus/utils/useDeviceStatusSignalR';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeImageField from '@/components/forms/ImageField/VeeImageField.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import DeviceLicenseInline from '@/components/deviceStatus/DeviceLicenseInline.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { mainNavigationQaEquipment, serialNumber } from '@/components/icons/icons';
import { NotificationMessage } from '@/components/notifications/types';
import { useNotificationStore } from '@/stores/notificationStore';
import { useLicenseStore } from '@/stores/licenseStore';
import { isActionEnabled } from '@/utils/actions';
import { I18nKey } from '@/utils/typings';
import QaEquipmentDeRegistrationSection from '@/views/qaEquipment/components/QaEquipmentDeRegistrationSection.vue';
import QaEquipmentIotInformationSection from '@/views/qaEquipment/components/QaEquipmentIotInformationSection.vue';
import QaEquipmentRegistrationSection from '@/views/qaEquipment/components/QaEquipmentRegistrationSection.vue';
import UnicheckDeviceOfflineInfoDialog from '@/views/qaEquipment/components/UnicheckDeviceOfflineInfoDialog.vue';
import {
  getManufacturerLabel,
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils.ts';
import { MaskitoOptions } from '@maskito/core';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useDeviceError } from '@/components/deviceStatus/utils/useDeviceMessages';
import Notification from '@/components/notifications/Notification.vue';
import { isPredefinedEntity } from '@/utils/helper';
import { useDisplay } from 'vuetify';
import { createDetailImageProps } from '@/components/images/presets';

const values = useFormValues<QaEquipmentRtSystemDto>(); //| CreateQaEquipmentRtSystemDto
const mode = useFormMode();
const entityReferences = ref<ReferencingObjectDto[] | null>(null);
const { mdAndDown } = useDisplay();
const { t } = useTranslation();
const notificationStore = useNotificationStore();

const detailImageProps = createDetailImageProps({
  fallbackIcon: mainNavigationQaEquipment,
});

const canBeRegistered = computed(() => {
  return (
    values.value &&
    isRegistrablePtwQaEquipment(values.value) &&
    (values.value.registrationState === 'NotRegistered' ||
      values.value.registrationState === 'DeRegistered') &&
    isActionEnabled(values.value, 'Edit')
  );
});

const isRegistered = computed(
  () =>
    'registrationState' in values.value &&
    values.value.registrationState === PtwRegistrationState.Registered,
);
const isDeRegistered = computed(
  () =>
    'registrationState' in values.value &&
    values.value.registrationState === PtwRegistrationState.DeRegistered,
);
const isInUse = computed(() => values.value.isReferenced);

const maskOptions: MaskitoOptions = {
  mask: /^\d{1,5}$/,
};

const [isInfoDialogOpen, toggleIotInfoDialog] = useToggle(false);

const deviceId = computed(() =>
  instanceOfUnicheckDeviceDto(values.value) || instanceOfUnicheckSimulatorDto(values.value)
    ? values.value.id
    : undefined,
);

const licenseStore = useLicenseStore();
const licenseIssue = computed(() =>
  licenseStore.deviceLicenseIssue(values.value as QaEquipmentRtSystemDto),
);

const deviceState = useDeviceStatusSignalR(deviceId.value);
const { notifications: deviceNotifications } = useDeviceError(deviceState);

const isOffline = computed(() => deviceState.value?.state == IotDeviceStateOfUse.Offline);
const isMeasurementInProgress = computed(
  () =>
    deviceState.value?.state === IotDeviceStateOfUse.Measuring ||
    deviceState.value?.state === IotDeviceStateOfUse.Initializing,
);
const isAvailabilityDisabledBecauseInUse = computed(
  () => mode.value === 'edit' && Boolean(deviceId.value) && isMeasurementInProgress.value,
);

const availabilityDisabled = computed(
  () =>
    isAvailabilityDisabledBecauseInUse.value ||
    (mode.value === 'edit' && Boolean(licenseIssue.value) && !values.value.isAvailable),
);

const availabilityInfoMessage = computed(() => {
  if (isAvailabilityDisabledBecauseInUse.value) {
    return t('TheDeviceCanNotBeDerigsteredWhileInUse');
  }

  return licenseIssue.value ? t(licenseIssue.value) : undefined;
});

watch(isAvailabilityDisabledBecauseInUse, (isDisabledNow, wasDisabledBefore) => {
  if (isDisabledNow && !wasDisabledBefore) {
    notificationStore.warning(t('TheDeviceCanNotBeDerigsteredWhileInUse'));
  }
});

const firmware = computed(() =>
  deviceState.value?.deviceInformation?.firmwareVersion &&
  deviceState.value?.deviceInformation?.firmwareDate
    ? `${deviceState.value.deviceInformation.firmwareVersion} (${deviceState.value.deviceInformation.firmwareDate})`
    : undefined,
);
</script>

<template>
  <InlineNotification
    v-if="(values.isReferenced || isPredefinedEntity(values)) && mode === 'edit'"
    variant="flat"
    class="mb-4"
    :value="
      {
        type: 'info',
        message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
          entity: $t('DosimetryAndQaEquipment'),
        }),
      } satisfies NotificationMessage
    "
  >
    <template #append>
      <span
        v-if="values.isReferenced"
        class="ml-2 linkButton"
        @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
        >{{ $t('Info') }}</span
      >
      <EntityReferencesModal
        v-if="values.isReferenced"
        :references="entityReferences"
        :title="$t('ReferenceingEntities')"
        @dialogOpen="
          (isOpen: boolean) => {
            if (!isOpen) entityReferences = null;
          }
        "
      ></EntityReferencesModal>
    </template>
  </InlineNotification>
  <v-row>
    <v-col cols="6" lg="2">
      <VeeImageField
        v-bind="detailImageProps"
        name="imageId"
        :fallbackPictureId="values.defaultImageId"
        data-testid="dosimetryQaEquipmentEntityImage"
      />
    </v-col>
    <v-col cols="6" lg="2" class="order-lg-12">
      <VeeAvailability
        name="isAvailable"
        :isReferencedBy="values.isReferencedBy ?? undefined"
        :disabled="availabilityDisabled"
        :infoMessage="availabilityInfoMessage"
        :hideReferences="true"
        :impactMessage="
          $t('DisableQaEquipmentInfoMsg', {
            entityName: $t('DosimetryAndQaEquipment'),
          })
        "
      />
      <EntityChangeBlock v-if="mode === 'details'" :entity="values as QaEquipmentRtSystemDto" />
      <QaEquipmentDeRegistrationSection
        v-if="mode === 'details' && (isRegistered || isDeRegistered)"
      />
    </v-col>
    <v-col cols="12" lg="8">
      <v-row v-if="mode === 'details' && !isOffline && isRegistered">
        <div class="d-flex flex-column w-100 h-100">
          <Notification
            v-for="(notification, index) in deviceNotifications"
            :key="index"
            :value="notification"
            class="mb-4 ml-4"
          />
        </div>
      </v-row>
      <v-row>
        <v-col>
          <DeviceLicenseInline
            :device="values as QaEquipmentRtSystemDto"
            :variant="mdAndDown ? 'icon-only' : 'default'"
          />
        </v-col>
      </v-row>
      <v-row v-if="mode === 'details' && canBeRegistered">
        <v-col>
          <QaEquipmentRegistrationSection />
        </v-col>
      </v-row>
      <v-row v-if="mode === 'details' && deviceId && isOffline && isRegistered">
        <v-col>
          <QaEquipmentIotInformationSection
            :entityId="deviceId"
            @openDialog="toggleIotInfoDialog(true)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" lg="4">
          <VeeTextField name="name" :label="$t('Name')" :placeholder="$t('Required')" />
          <VeeTextarea class="gutterTop" name="description" :label="$t('Description')" rows="3" />
          <VeeAttachments class="mt-1" name="attachments" data-testid="Description_Attachments" />
        </v-col>
        <v-col cols="6" lg="4">
          <VeeTextField
            v-if="'serialNumber' in values"
            class="gutterTop"
            name="serialNumber"
            :infoMessage="
              isDeRegistered || isInUse
                ? $t('SerialNumberDisabledReasonTooltipMsg')
                : $t('SerialNumberTooltipMsg')
            "
            :label="$t('SerialNumber')"
            :placeholder="$t('Required')"
            :disabled="mode === 'edit' && (isRegistered || isDeRegistered || isInUse)"
            :prependInnerIcon="serialNumber"
          />
          <VeeTextField
            v-if="'dpin' in values"
            v-maskito="maskOptions"
            class="gutterTop"
            name="dpin"
            :infoMessage="
              isDeRegistered || isInUse ? $t('DpinDisabledReasonTooltipMsg') : $t('DpinTooltipMsg')
            "
            :label="$t('DPIN')"
            :placeholder="$t('Required')"
            :disabled="mode === 'edit' && (isRegistered || isDeRegistered || isInUse)"
          />
          <VeeTextField
            v-if="'identification' in values"
            class="gutterTop"
            name="identification"
            :label="$t('Identification')"
          />

          <div v-if="deviceId && isOffline && mode === 'details'" class="gutterTop">
            <p class="text-body-2 text-primary-lighten-1">
              {{ $t('DeviceIsOffline', { deviceName: 'UNICHECK' }) }}
            </p>
            <p class="text-body-2 text-primary-lighten-1">
              {{ $t('DeviceInformationOfflineMsg') }}
            </p>
          </div>

          <div v-if="deviceId && !isOffline && deviceState?.deviceInformation" class="gutterTop">
            <TextField
              :label="$t('DeviceInformationDeviceVersion')"
              mode="details"
              :modelValue="
                deviceState?.deviceInformation?.interfaceboardHardwareVersion || undefined
              "
            />
            <TextField
              :label="$t('DeviceInformationMainboardVersion')"
              class="gutterTop"
              mode="details"
              :modelValue="deviceState?.deviceInformation?.mainboardHardwareVersion || undefined"
            />
            <TextField
              :label="$t('DeviceInformationFirmwareVersion')"
              class="gutterTop"
              mode="details"
              :modelValue="firmware"
            />
          </div>
        </v-col>
        <v-col cols="6" lg="4">
          <TextField
            name="subCategory"
            :label="$t('Type_one')"
            :modelValue="$t(getTypeLabel(values) as I18nKey)"
            mode="details"
          />

          <TextField
            :label="$t('Manufacturer')"
            :modelValue="$t(getManufacturerLabel(values) as I18nKey)"
            class="gutterTop"
            mode="details"
          />

          <TextField
            :label="$t('Model') + (values.subModel ? ` | ${$t('SubModel')}` : '')"
            :modelValue="getModelLabel(values)"
            class="gutterTop"
            mode="details"
          />

          <TextField
            v-if="'typeNumber' in values && values.typeNumber"
            :label="$t('ArticleNumber')"
            :modelValue="values.typeNumber"
            class="gutterTop"
            mode="details"
          />

          <TextField
            v-if="
              'singleDetectorConnectorType' in values &&
              values.singleDetectorConnectorType &&
              values.singleDetectorConnectorType !== 'None'
            "
            :label="$t('SingleDetectorConnectorTypeLabel')"
            class="gutterTop"
            :modelValue="
              $t(`SingleDetectorConnectorTypeEnum.${values.singleDetectorConnectorType}` as I18nKey)
            "
            mode="details"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>

  <VeeTextField v-if="'version' in values" name="version" :label="$t('Version')" />
  <VeeTextField
    v-if="'dicomMachineName' in values"
    name="dicomMachineName"
    :label="$t('DicomTreatmentMachineName')"
  />
  <UnicheckDeviceOfflineInfoDialog
    v-if="deviceId"
    v-model="isInfoDialogOpen"
    :isOffline="isOffline"
  />
</template>

<style lang="scss" scoped>
:deep(.v-field__prepend-inner) {
  .v-icon {
    width: 32px;
    height: 32px;
  }
}
</style>
